import { Box, BoxProps, Stack } from '@chakra-ui/react';
import { Card } from '@main/complykit/components/card';
import { Heading } from '@main/complykit/components/heading';
import { Content, Header } from '@main/complykit/components/slots';
import { Text } from '@main/complykit/components/text';
import { Children, isValidElement, ReactNode, useMemo } from 'react';

/**
 * Sizing and layout stay on a wrapping Box, because ComplyKit's Card owns its
 * appearance and takes no style props.
 *
 * The wrapper has to be transparent to layout: dashboards put these in a grid
 * row and pass h="full" expecting every card to fill it, so the wrapper
 * stretches and the Card fills the wrapper. Without that the cards size to
 * their own content and the row ends up ragged.
 */
export type DashboardCardProps = BoxProps;

/** Chakra spacing steps the callers pass, mapped to the card's own inset. */
const PADDING_STEP = { 2: 'compact', 4: 'default', 6: 'spacious' } as const;

export const DashboardCard = ({ children, p, padding, ...box }: DashboardCardProps) => {
  /* Padding is the card's, not the wrapper's. Leaving `p` on the wrapper
   * applied the inset twice: once outside the border and once in the slot,
   * which pushed the cards apart and narrowed them. */
  const step = PADDING_STEP[(p ?? padding) as keyof typeof PADDING_STEP] ?? 'spacious';

  /* Widgets that pass their content straight in, with no .Header or .Body,
   * still expect the card's inset, so wrap those children in a slot rather
   * than letting them sit flush against the border. */
  const hasSlot = Children.toArray(children).some(
    (child) =>
      isValidElement(child) &&
      (child.type === DashboardCardHeader || child.type === DashboardCardBody),
  );

  return (
    <Box display="flex" flexDirection="column" {...box}>
      <Card padding={step} isFilled>
        {hasSlot ? children : <Content>{children}</Content>}
      </Card>
    </Box>
  );
};

export type DashboardCardHeaderProps = {
  heading: ReactNode;
  subheading?: ReactNode;
  children?: (props: { header: ReactNode }) => ReactNode;
};

const DashboardCardHeader = ({ heading, subheading, children }: DashboardCardHeaderProps) => {
  const header = useMemo(
    () => (
      <Stack spacing={1}>
        <Heading as="h3" size="md">
          {heading}
        </Heading>
        {subheading && (
          <Text size="sm" color="muted">
            {subheading}
          </Text>
        )}
      </Stack>
    ),
    [heading, subheading],
  );

  const content = useMemo(() => children?.({ header }) ?? header, [children, header]);

  return <Header>{content}</Header>;
};
DashboardCard.Header = DashboardCardHeader;

export type DashboardCardBodyProps = BoxProps;

const DashboardCardBody = ({ children, maxH, minH, overflowY, ...box }: DashboardCardBodyProps) => {
  /* Scrolling and the height cap belong to the slot, not to a Box inside it.
   * Nested in the padding they clipped against the inset and scrolled content
   * that visibly fitted, which is what made a two-card list scrollable. */
  const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
  const hasLayout = Object.keys(box).length > 0;

  /* The floor belongs to the slot for the same reason the cap does. A chart
   * measures the box it is given, so a minimum height on an inner element
   * leaves the slot free to collapse and the plot draws into no vertical
   * space at all: every series lands on the same line. */
  return (
    <Content
      isScrollable={isScrollable}
      maxHeight={maxH as string | number | undefined}
      minHeight={minH as string | number | undefined}
    >
      {hasLayout ? <Box {...box}>{children}</Box> : children}
    </Content>
  );
};
DashboardCard.Body = DashboardCardBody;
